import React from 'react'
import styled, { css } from 'styled-components'
import { NewButtonA } from 'components/elements/NewButton'
import { Body1, ButtonText, Headline3, Headline4, Subtitle1 } from 'stories/elements/Typography/Text'
import theme from 'stories/utils/theme'
import MailIcon from 'assets/for-companies/mail.svg'
import _useTranslation from 'lib/hooks/_useTranslation'

const Container = styled.div<{ backgroundColor?: string; addGradient?: boolean }>`
    min-height: 500px;
    @media (max-width: 750px) {
        height: auto;
    }
    background-color: ${({ backgroundColor }) => backgroundColor ?? theme.colors.accentBlue};
    color: ${theme.colors.white};
    text-align: left;

    ${({ addGradient }) =>
        addGradient
            ? css`
                  background: rgb(82, 240, 141);
                  background: linear-gradient(140deg, rgba(82, 240, 141, 1) 0%, rgba(41, 98, 255, 1) 100%);
                  background-blend-mode: lighten;
              `
            : ''}
    & section {
        display: flex;
        flex-direction: column;
        align-items: center;
    }

    .credential {
        display: grid;
        grid-template-columns: 1fr;
        place-content: start;
        grid-gap: 0px;
        .contact {
            display: flex;

            gap: 10px;
            svg {
                position: relative;
                bottom: 2px;
            }
        }
    }

    & .right {
        display: grid;
        grid-template-columns: auto 1fr;
        grid-gap: 75px;
    }

    & img {
        max-width: 164px;
        border-radius: 100%;
        border: 4px solid ${theme.colors.white};
        z-index: 2;
        background-repeat: no-repeat;
        background-position: center center;
        background-size: fill;
    }

    & a,
    span {
        color: ${theme.colors.white};
        font-size: 15px;
        font-style: normal;
        font-weight: 600;
        line-height: 23px;
        text-align: left;
    }

    @media (max-width: 750px) {
        & section {
            grid-template-columns: 1fr;
        }

        & img {
            max-width: 50%;
            height: auto;
        }

        & .right {
            place-items: center;
            grid-template-columns: 1fr;
            grid-gap: 45px;

            & div {
                text-align: left;
                display: grid;
                place-items: center;
            }
        }
    }
`

interface DetailInterface {
    headingText: string
    image: StaticImageData | string
    name: string
    email: string
    buttonLink: string
    phoneNumber?: string
    buttonText?: string
    subtitle?: string
    employeeTitle?: string
}

interface Props {
    detail: DetailInterface
    isWhiteColor?: boolean
    backgroundColor?: string
    onContactButtonClick?: () => void
    addGradient?: boolean
}

const ContactPerson = ({ detail, isWhiteColor, backgroundColor, onContactButtonClick, addGradient }: Props) => {
    const { t } = _useTranslation('common')

    return (
        <Container backgroundColor={backgroundColor} addGradient={addGradient}>
            <section>
                <Subtitle1 color={theme.colors.white} textAlign="center" fontFamily="var(--font-inter)" fontWeight={500}>
                    {detail.subtitle ?? t('common/footerLegalContact')}
                </Subtitle1>
                <Headline3
                    color={theme.colors.white}
                    textAlign="center"
                    marginBottom={100}
                    fontWeight={600}
                    fontFamily="var(--font-poppins)"
                >
                    {detail.headingText}
                </Headline3>
                <div className="right">
                    <img
                        height={164}
                        width={164}
                        src={typeof detail.image === 'string' ? detail.image : detail.image.src}
                        alt="Your knowunity contact person"
                    />
                    <div className="credential">
                        <Headline4
                            color={theme.colors.white}
                            marginBottom={0}
                            fontFamily="var(--font-open-sans)"
                            fontWeight={600}
                        >
                            {detail.name}
                        </Headline4>
                        {detail.employeeTitle ? (
                            <Body1
                                color={theme.colors.lightWhite}
                                marginBottom={20}
                                fontFamily="var(--font-inter)"
                                fontWeight={400}
                            >
                                {detail.employeeTitle}
                            </Body1>
                        ) : null}
                        {detail.phoneNumber ? (
                            <Subtitle1 color={theme.colors.white}>
                                <span>{detail.phoneNumber}</span>
                            </Subtitle1>
                        ) : null}

                        <a href={'mailto:' + detail.email} className="contact">
                            <MailIcon />{' '}
                            <Subtitle1
                                marginBottom={15}
                                color={theme.colors.white}
                                fontFamily="var(--font-inter)"
                                fontWeight={400}
                            >
                                {detail.email}
                            </Subtitle1>
                        </a>
                        <div />

                        <span onClick={() => onContactButtonClick && onContactButtonClick()}>
                            <NewButtonA
                                href={detail.buttonLink}
                                fullWidth
                                width={250}
                                height={50}
                                type={isWhiteColor ? 'white' : 'primary'}
                                borderRadius={theme.borderRadius.normal}
                            >
                                <ButtonText color={isWhiteColor ? theme.colors.accentBlue : theme.colors.white}>
                                    {detail?.buttonText ?? t('common/footerLegalContact')}
                                </ButtonText>
                            </NewButtonA>
                        </span>
                    </div>
                </div>
            </section>
        </Container>
    )
}

export default ContactPerson
